import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import api from './api';

const WorkflowContext = createContext(null);

// Normalize sales order ID to string for consistent comparison
function normalizeId(id) {
  if (!id) return null;
  const str = String(id).trim();
  // Filter out temporary/pending IDs that aren't real SAP order numbers
  if (str === 'None' || str === 'null' || str === 'undefined' || 
      str.startsWith('PENDING-') || str.startsWith('NEW-')) {
    return null;
  }
  return str;
}

// Map agent names to their roles
const AGENT_ROLE_MAP = {
  'Sales Order Agent': 'sales_order_agent',
  'Delivery Agent': 'delivery_agent',
  'AR Agent': 'ar_agent',
  'EDI Agent': 'edi_agent',
  'AP Agent': 'ap_agent',
  'Resource Agent': 'resource_agent'
};

// Map step descriptions to document types
const STEP_TO_DOC_TYPE = {
  'Sales Order Created': 'Sales Order',
  'Creating Sales Order': 'Sales Order',
  'PO Created': 'Purchase Order',
  'Creating Purchase Order': 'Purchase Order',
  'GR Created': 'Goods Receipt',
  'Creating Goods Receipt': 'Goods Receipt',
  'Awaiting Resource Agent Response': 'Goods Receipt',
  'Creating Billing Document': 'Billing Document',
  'Awaiting Customer Payment': 'Billing Document',
  'Billing Document Created': 'Billing Document',
  'Processing EDI 810 Invoice': 'EDI 810 Invoice',
  'EDI 810 Invoice Processed': 'EDI 810 Invoice',
  'Creating Supplier Invoice': 'Supplier Invoice',
  'Supplier Invoice Created': 'Supplier Invoice',
  'Processing Supplier Invoice': 'Supplier Invoice',
  'Clearing Outgoing Payment': 'Outgoing Payment',
  'Outgoing Payment Cleared': 'Outgoing Payment',
  'MIRO + Payment Cleared': 'Outgoing Payment',
  'AP Process Complete': 'Outgoing Payment',
  'Clearing Customer Payment': 'Payment Clearing',
  'Payment Cleared': 'Payment Clearing'
};

export function WorkflowProvider({ children }) {
  // Use a Map internally for O(1) lookups and guaranteed uniqueness
  const [workflowMap, setWorkflowMap] = useState(new Map());
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [sseConnected, setSseConnected] = useState(false);
  
  const sseRef = useRef(null);
  const selectedWorkflowIdRef = useRef(selectedWorkflowId);
  const apiLoadInProgress = useRef(false);
  
  useEffect(() => {
    selectedWorkflowIdRef.current = selectedWorkflowId;
  }, [selectedWorkflowId]);

  // Convert Map to array for consumers
  const workflows = useMemo(() => {
    return Array.from(workflowMap.values()).sort((a, b) => {
      // Sort by started_at descending (newest first)
      const aTime = new Date(a.started_at || 0).getTime();
      const bTime = new Date(b.started_at || 0).getTime();
      return bTime - aTime;
    });
  }, [workflowMap]);

  // Get the currently selected workflow
  const selectedWorkflow = useMemo(() => {
    if (!selectedWorkflowId) return null;
    return workflowMap.get(selectedWorkflowId) || null;
  }, [workflowMap, selectedWorkflowId]);

  // Load all workflows from API (summaries only - fast)
  const loadWorkflows = useCallback(async () => {
    if (apiLoadInProgress.current) {
      console.log('[WF] API load already in progress, skipping');
      return;
    }
    
    try {
      apiLoadInProgress.current = true;
      setLoading(true);
      console.log('[WF] Loading workflow summaries...');
      const response = await api.get('/workflow/recent?limit=50');
      const data = response.data || [];
      
      setWorkflowMap(prevMap => {
        const newMap = new Map(prevMap);
        
        data.forEach(w => {
          const id = normalizeId(w.sales_order_id);
          if (!id) return;
          
          const existing = newMap.get(id);
          
          // Summary data - don't overwrite detailed steps from SSE/detail fetch
          const merged = {
            ...(existing || {}),
            sales_order_id: id,
            status: w.status || existing?.status || 'in_progress',
            completed_steps: Math.max(w.completed_steps || 0, existing?.completed_steps || 0),
            total_steps: w.total_steps || 8,
            current_step: w.current_step || existing?.current_step,
            started_at: w.started_at || existing?.started_at,
            last_updated: w.last_updated || existing?.last_updated,
            // Preserve detailed steps if already loaded
            steps: existing?.steps || [],
            _lastApiUpdate: Date.now()
          };
          
          newMap.set(id, merged);
        });
        
        console.log('[WF] Loaded', data.length, 'summaries, map size:', newMap.size);
        return newMap;
      });
      
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[WF] loadWorkflows error:', err);
    } finally {
      setLoading(false);
      apiLoadInProgress.current = false;
    }
  }, []);

  // Load details for a specific workflow
  const loadWorkflowDetails = useCallback(async (salesOrderId) => {
    const id = normalizeId(salesOrderId);
    if (!id) return null;
    
    try {
      console.log('[WF] Loading details for', id);
      const response = await api.get(`/workflow/sales-order/${id}`);
      const detailedData = response.data;
      console.log('[WF] Got', detailedData.steps?.length || 0, 'steps from API');
      
      setWorkflowMap(prevMap => {
        const newMap = new Map(prevMap);
        const existing = newMap.get(id);
        
        newMap.set(id, {
          ...(existing || {}),
          ...detailedData,
          sales_order_id: id,
          steps: detailedData.steps || [],
          _lastApiUpdate: Date.now()
        });
        
        return newMap;
      });
      
      return detailedData;
    } catch (err) {
      console.error('[WF] loadWorkflowDetails error:', err);
      return null;
    }
  }, []);

  // Select a workflow - always load details since list is summary-only
  const selectWorkflow = useCallback(async (salesOrderId) => {
    const id = normalizeId(salesOrderId);
    if (!id) return;
    
    console.log('[WF] Selecting workflow:', id);
    setSelectedWorkflowId(id);
    setDetailLoading(true);
    await loadWorkflowDetails(id);
    setDetailLoading(false);
  }, [loadWorkflowDetails]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedWorkflowId(null);
    setDetailLoading(false);
  }, []);

  // Force refresh for the currently selected workflow
  const refreshDetailedView = useCallback(async () => {
    const currentId = selectedWorkflowIdRef.current;
    if (!currentId) return;
    
    console.log('[WF] Force refreshing detail view for:', currentId);
    setDetailLoading(true);
    
    try {
      await loadWorkflowDetails(currentId);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[WF] Force refresh error:', err);
    } finally {
      setDetailLoading(false);
    }
  }, [loadWorkflowDetails]);

  // Process SSE message and update workflow
  const processSSEMessage = useCallback((message) => {
    const soId = normalizeId(message.sales_order_id);
    if (!soId) return;
    
    const data = message.data || {};
    const agentName = data.agent || data.agent_name || 'Agent';
    const step = data.step || data.document_type || 'Processing';
    const completedSteps = data.completed_steps || 0;
    const status = data.status || 'in_progress';
    
    // Get document_type
    let docType = data.document_type;
    if (!docType && step) {
      docType = STEP_TO_DOC_TYPE[step];
    }
    
    // Get document ID - be smart about which field to use based on document type
    // This prevents mixing up PO numbers with GR numbers, etc.
    let docId = data.document_id; // Primary field, always preferred
    if (!docId && docType) {
      // Use the field that matches the document type
      switch (docType) {
        case 'Sales Order':
          docId = data.sales_order_id || data.sap_order_id;
          break;
        case 'Purchase Order':
          docId = data.po_number || data.purchase_order_number;
          break;
        case 'Goods Receipt':
          docId = data.gr_number || data.goods_receipt_number;
          break;
        case 'Billing Document':
          docId = data.billing_doc || data.billing_doc_number;
          break;
        case 'EDI 810 Invoice':
          docId = data.invoice_number || data.parsed_invoice_number;
          break;
        case 'Supplier Invoice':
          docId = data.invoice_number || data.sap_invoice_number;
          break;
        case 'Outgoing Payment':
        case 'Payment Clearing':
          docId = data.clearing_doc || data.payment_advice_number || data.clearing_document;
          break;
        default:
          // Fallback to any available ID
          docId = data.po_number || data.gr_number || data.invoice_number;
      }
    }

    console.log('[SSE]', message.type, soId, agentName, '|', step, '| docType:', docType, '| docId:', docId, '| status:', status);

    // Determine step status
    let stepStatus = 'running';
    if (status === 'completed') {
      stepStatus = 'completed';
    } else if (status === 'awaiting_payment') {
      stepStatus = 'awaiting_payment';
    }

    // Update workflow map
    setWorkflowMap(prevMap => {
      const newMap = new Map(prevMap);
      const existing = newMap.get(soId);
      
      if (existing) {
        // Update existing workflow
        let updatedSteps = [...(existing.steps || [])];
        
        if (docType) {
          const existingStepIdx = updatedSteps.findIndex(s => s.document_type === docType);
          
          if (existingStepIdx >= 0) {
            const existingStep = updatedSteps[existingStepIdx];
            // Never downgrade from completed to running
            const shouldUpdateStatus = !(existingStep.status === 'completed' && stepStatus === 'running');
            
            updatedSteps[existingStepIdx] = {
              ...existingStep,
              status: shouldUpdateStatus ? stepStatus : existingStep.status,
              document_number: docId || existingStep.document_number,
              completed_at: stepStatus === 'completed' ? new Date().toISOString() : existingStep.completed_at,
              _sseUpdated: true
            };
          } else {
            // Add new step
            updatedSteps.push({
              agent_name: agentName,
              agent_role: AGENT_ROLE_MAP[agentName] || agentName.toLowerCase().replace(/\s+/g, '_'),
              document_type: docType,
              document_number: docId,
              status: stepStatus,
              started_at: new Date().toISOString(),
              completed_at: stepStatus === 'completed' ? new Date().toISOString() : null,
              step_number: updatedSteps.length + 1,
              _sseUpdated: true
            });
          }
        }
        
        // Calculate workflow status
        let workflowStatus = 'in_progress';
        if (completedSteps === 8) {
          workflowStatus = 'completed';
        } else if (status === 'awaiting_payment') {
          workflowStatus = 'awaiting_payment';
        }
        
        newMap.set(soId, {
          ...existing,
          current_step: `${agentName}: ${step}`,
          completed_steps: Math.max(completedSteps, existing.completed_steps || 0),
          status: workflowStatus,
          steps: updatedSteps,
          _lastSSEUpdate: Date.now()
        });
      } else if (message.type === 'new' || (message.type === 'update' && completedSteps >= 1)) {
        // Create new workflow entry for:
        // - Explicit "new" events (workflow just started)
        // - "update" events with real progress (safety net if "new" was missed)
        // Skip "step_started" events for unknown workflows (likely old email reprocessing)
        console.log('[SSE] Adding new workflow:', soId, 'via', message.type);
        const newStep = docType ? {
          agent_name: agentName,
          agent_role: AGENT_ROLE_MAP[agentName] || agentName.toLowerCase().replace(/\s+/g, '_'),
          document_type: docType,
          document_number: docId,
          status: stepStatus,
          started_at: new Date().toISOString(),
          step_number: 1,
          _sseUpdated: true
        } : null;
        
        newMap.set(soId, {
          sales_order_id: soId,
          status: 'in_progress',
          current_step: `${agentName}: ${step}`,
          completed_steps: completedSteps,
          total_steps: 8,
          steps: newStep ? [newStep] : [],
          started_at: new Date().toISOString(),
          _lastSSEUpdate: Date.now()
        });
      } else {
        // Non-"new" event for unknown workflow — ignore (likely old email reprocessing)
        console.log('[SSE] Ignoring', message.type, 'for unknown workflow:', soId);
      }
      
      return newMap;
    });

    // Toast notifications
    if (message.type === 'step_started' || message.type === 'new') {
      toast(
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center text-white">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900 text-sm">SO #{soId}</p>
            <p className="text-xs text-slate-500">{agentName}: {step}</p>
          </div>
          <span className="text-xs text-teal-600 font-medium">{completedSteps}/8</span>
        </div>,
        { duration: 4000, id: `wf-${soId}-${completedSteps}` }
      );
    }

    if (completedSteps === 8 && status === 'completed') {
      toast.success(
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white">
            <CheckCircle className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-slate-900 text-sm">SO #{soId} Complete</p>
            <p className="text-xs text-slate-500">All 8 steps finished</p>
          </div>
        </div>,
        { duration: 6000, id: `wf-complete-${soId}` }
      );
    }

    setLastRefresh(new Date());
  }, []);

  // SSE Connection - with improved stability
  useEffect(() => {
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    let isIntentionallyClosed = false;

    const connectSSE = () => {
      if (isIntentionallyClosed) return;
      
      try {
        const sseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/workflow/stream`;
        console.log('[SSE] Connecting to', sseUrl);
        
        // Close existing connection if any
        if (sseRef.current) {
          sseRef.current.close();
        }
        
        sseRef.current = new EventSource(sseUrl);

        sseRef.current.onopen = () => {
          console.log('[SSE] Connected');
          setSseConnected(true);
          reconnectAttempts = 0;
        };

        sseRef.current.onmessage = (event) => {
          try {
            // Any message means we're connected
            setSseConnected(true);
            
            const message = JSON.parse(event.data);
            
            if (message.type === 'connected') {
              console.log('[SSE] Received connection confirmation');
              return;
            }

            processSSEMessage(message);
          } catch (e) {
            // Ignore parse errors (could be keepalive comments)
          }
        };

        sseRef.current.onerror = (e) => {
          // Only log and reconnect if not intentionally closed
          if (isIntentionallyClosed) return;
          
          console.log('[SSE] Connection error, will reconnect...');
          
          // Don't immediately set disconnected - give it a moment
          // This prevents flickering during brief reconnections
          setTimeout(() => {
            if (sseRef.current?.readyState === EventSource.CLOSED) {
              setSseConnected(false);
            }
          }, 1000);
          
          if (sseRef.current) {
            sseRef.current.close();
            sseRef.current = null;
          }
          
          reconnectAttempts++;
          // Faster reconnect: start at 500ms, max 10 seconds
          const delay = Math.min(500 * Math.pow(1.5, reconnectAttempts), 10000);
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
          reconnectTimeout = setTimeout(connectSSE, delay);
        };

      } catch (e) {
        console.error('[SSE] Connection error:', e);
        reconnectTimeout = setTimeout(connectSSE, 2000);
      }
    };

    // Initial load from API
    loadWorkflows();
    
    // Connect SSE
    connectSSE();

    return () => {
      isIntentionallyClosed = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [loadWorkflows, processSSEMessage]);

  const value = {
    workflows,
    selectedWorkflow,
    selectedWorkflowId,
    loading,
    detailLoading,
    lastRefresh,
    sseConnected,
    selectWorkflow,
    clearSelection,
    refresh: loadWorkflows,
    refreshDetailedView,
    loadWorkflowDetails
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
}

export function useWorkflows() {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error('useWorkflows must be used within WorkflowProvider');
  }
  return context;
}
